import type { ClusterMarker, ClusterStatus } from '@/services/consumerService'

// Cached clusters and fresh status meet by grid cell. The zoom is part of the
// key because the same cellX/cellY names a different area at another zoom.
export type StatusByCell = ReadonlyMap<string, ClusterStatus>

// Both counts come from the same status row. The cached cluster's pointCount
// can be older, so it is never paired with a fresh alertCount.
export type AlertCounts = Pick<ClusterStatus, 'pointCount' | 'alertCount'>

export type AlertState = 'NONE' | 'PARTIAL' | 'FULL'

export const alertStateOf = (counts?: AlertCounts): AlertState => {
  if (!counts || counts.alertCount === 0) return 'NONE'
  return counts.alertCount === counts.pointCount ? 'FULL' : 'PARTIAL'
}

export type MapCell = ClusterMarker & {
  alerts?: AlertCounts
  lastReadingAt?: string | null
}

const cellKey = (zoom: number, cellX: number, cellY: number) =>
  `${zoom}/${cellX}/${cellY}`

export const NO_STATUS: StatusByCell = new Map()

export const indexStatus = (
  zoom: number,
  status: ClusterStatus[]
): StatusByCell =>
  new Map(status.map(s => [cellKey(zoom, s.cellX, s.cellY), s]))

// A cell with no status yet has no alerts and no last reading time.
export const mergeStatus = (
  clusters: ClusterMarker[],
  zoom: number,
  status: StatusByCell
): MapCell[] =>
  clusters.map(cluster => {
    const live = status.get(cellKey(zoom, cluster.cellX, cluster.cellY))
    return {
      ...cluster,
      alerts: live && {
        pointCount: live.pointCount,
        alertCount: live.alertCount,
      },
      lastReadingAt: live?.lastReadingAt,
    }
  })
